import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/config';
import { useChat } from '../../contexts/ChatContext';
import { getGroupCandidates } from '../../services/moodle/chat';
import GroupManager from './GroupManager';

// PARTIAL FEATURE — see the note on the group row menu in ChatContainer.
// This screen covers rename + membership only. Deleting a group is not
// implemented anywhere yet (no delete endpoint exists in the plugin), so a
// group created by mistake can be emptied down to one member and renamed,
// but never removed from the list.
//
// Container: owns candidate fetch, the initial-vs-current member diff, name
// state and save. Mirrors GroupComposerContainer — candidates come straight
// from the service, not from ChatContext (which only carries boolean gates).
const GroupManagerContainer = ({ group, courseId, onBack }) => {
  const { user } = useAuth();
  const { config } = useConfig();
  const { updateGroupChat } = useChat();

  const [students, setStudents] = useState([]);
  const [maxMembers, setMaxMembers] = useState(40);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [name, setName] = useState(group?.fullname || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Baseline the diff is computed against. Only ids the candidate list can
  // actually represent are tracked: a co-teacher who is a member but not a
  // course student never appears in the picker, so it must never look like
  // the tutor deselected them.
  const initialMemberIds = useMemo(() => {
    const candidateIds = new Set(students.map(s => Number(s.id)));
    return (group?.members || [])
      .map(m => Number(m.id))
      .filter(id => candidateIds.has(id));
  }, [group, students]);

  useEffect(() => {
    let cancelled = false;

    const loadCandidates = async () => {
      if (!user || !user.token || !courseId) return;
      setCandidatesLoading(true);
      try {
        const data = await getGroupCandidates(user.token, config.baseURL, courseId);
        if (cancelled) return;
        setStudents(data.students || []);
        setMaxMembers(data.maxmembers || 40);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading group candidates:', err);
        setError('No se pudieron cargar los alumnos. Intenta nuevamente.');
      } finally {
        if (!cancelled) setCandidatesLoading(false);
      }
    };

    loadCandidates();
    return () => {
      cancelled = true;
    };
  }, [user, config, courseId]);

  // Seed the selection once candidates land, so current members show checked.
  useEffect(() => {
    setSelectedIds(initialMemberIds);
  }, [initialMemberIds]);

  const handleToggleMember = useCallback((id) => {
    setSelectedIds(prev => (
      prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
    ));
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(students.map(s => Number(s.id)));
  }, [students]);

  const handleClearAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const addIds = selectedIds.filter(id => !initialMemberIds.includes(id));
  const removeIds = initialMemberIds.filter(id => !selectedIds.includes(id));
  const nameChanged = name.trim() !== (group?.fullname || '').trim();
  const dirty = nameChanged || addIds.length > 0 || removeIds.length > 0;

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      await updateGroupChat(courseId, group.conversationid, {
        name: nameChanged ? name.trim() : null,
        addIds,
        removeIds,
      });
      setSuccessMessage('Grupo actualizado');
      setTimeout(() => {
        onBack();
      }, 1200);
    } catch (err) {
      console.error('Error updating group:', err);
      // Keep the screen open with the edits intact — retyping a name and
      // re-ticking 20 students because one request failed is not acceptable.
      setError(err.message || 'No se pudieron guardar los cambios. Intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  if (successMessage) {
    return (
      <div className="w-full flex flex-col h-full items-center justify-center gap-3 p-6 text-center">
        <div className="text-2xl">✅</div>
        <p className="text-xl font-semibold">{successMessage}</p>
      </div>
    );
  }

  return (
    <GroupManager
      onBack={onBack}
      name={name}
      onNameChange={setName}
      students={students}
      candidatesLoading={candidatesLoading}
      selectedIds={selectedIds}
      onToggleMember={handleToggleMember}
      onSelectAll={handleSelectAll}
      onClearAll={handleClearAll}
      maxMembers={maxMembers}
      saving={saving}
      error={error}
      dirty={dirty}
      onSave={handleSave}
    />
  );
};

export default GroupManagerContainer;
